import type { ChartModel } from './types';
import { formatUtc } from './time';
import { formatEventDuration } from './tooltip-format';

interface HighchartsAxis {
  addPlotLine(options: Record<string, unknown>): HighchartsPlotLine | undefined;
  getExtremes(): { min?: number; max?: number };
  toValue(pixel: number, paneCoordinates?: boolean): number;
  removePlotLine(id: string): void;
  setExtremes(min: number, max: number, redraw?: boolean, animation?: boolean, eventArguments?: Record<string, unknown>): void;
}

interface HighchartsPlotLine {
  svgElem?: { element?: SVGElement };
}

interface HighchartsChart {
  container?: HTMLElement;
  contextMenu?: (...args: unknown[]) => void;
  destroy(): void;
  exportSVGElements?: HighchartsControlElement[];
  inverted?: boolean;
  openMenu?: boolean;
  plotLeft?: number;
  plotHeight?: number;
  plotTop?: number;
  plotWidth?: number;
  pointer?: { reset(allowMove?: boolean, delay?: number): void };
  reflow(): void;
  resetZoomButton?: HighchartsControlElement;
  showResetZoom?: () => void;
  xAxis?: HighchartsAxis[];
  series?: HighchartsSeries[];
  _pqEventTooltipPoint?: HighchartsPoint;
  _pqEventMarkerRectangles?: ChartRectangle[];
}

interface HighchartsControlElement {
  element?: Element;
  on?: (eventType: string, handler: (event?: Event) => void) => HighchartsControlElement;
  setState?: (state: number) => void;
}

interface HighchartsSeries {
  chart?: HighchartsChart;
  color?: string;
  name?: string;
  options?: { tooltip?: { valueSuffix?: string } };
  points?: HighchartsPoint[];
  visible?: boolean;
  xAxis?: { left?: number };
  yAxis?: { top?: number };
}

interface HighchartsPoint {
  color?: string;
  x?: number;
  y?: number;
  duration_ms?: number;
  duration_display?: string;
  classification?: string;
  classification_suffix?: string;
  selected?: boolean;
  options?: {
    x?: number;
    y?: number;
    duration_ms?: number;
    duration_display?: string;
    classification?: string;
    classification_suffix?: string;
  };
  plotX?: number;
  plotY?: number;
  series?: HighchartsSeries;
  graphic?: { element?: SVGElement };
  firePointEvent?: (eventType: string, event?: Event) => void;
  select?: (selected?: boolean, accumulate?: boolean) => void;
}

interface EventMarkerBinding {
  point: HighchartsPoint;
  keydownListener?: (event: KeyboardEvent) => void;
  mousemoveListener: (event: MouseEvent) => void;
}

const eventMarkerBindings = new WeakMap<SVGElement, EventMarkerBinding>();
const EVENT_TOOLTIP_MARKER_CLEARANCE = 4;
// Event markers render at radius 6 and enlarge to radius 9 on hover/select.
// Ten pixels is deliberately conservative while keeping tooltip positioning
// independent of synchronous SVG layout reads.
const EVENT_TOOLTIP_MARKER_RADIUS = 10;

interface HighchartsTooltipAnchor {
  negative?: boolean;
  plotX: number;
  plotY: number;
  ttBelow?: boolean;
}

interface HighchartsTooltipPosition {
  x: number;
  y: number;
}

interface HighchartsTooltipContext {
  chart: HighchartsChart;
  getPosition(width: number, height: number, point: HighchartsTooltipAnchor): HighchartsTooltipPosition;
  len: number;
}

interface ChartRectangle {
  bottom: number;
  left: number;
  right: number;
  top: number;
}

export interface TrendDataGapRange {
  after: number;
  before: number;
}

export function trendDataGapRanges(model: ChartModel): TrendDataGapRange[] {
  if (model.xAxisType !== 'datetime') return [];
  const samples = new Map<number, boolean>();
  for (const series of model.series) {
    if (series.visible === false
      || series.enableMouseTracking === false
      || series.yAxis === 1
      || (series.type != null && series.type !== 'line')) continue;
    for (const point of series.data) {
      if (!Array.isArray(point)) continue;
      const timestamp = point[0];
      if (typeof timestamp !== 'number' || !Number.isFinite(timestamp)) continue;
      const hasValue = point.slice(1).some((value) => typeof value === 'number' && Number.isFinite(value));
      samples.set(timestamp, samples.get(timestamp) === true || hasValue);
    }
  }

  const ordered = Array.from(samples).sort(([left], [right]) => left - right);
  const gaps: TrendDataGapRange[] = [];
  for (let index = 0; index < ordered.length;) {
    if (ordered[index][1]) {
      index += 1;
      continue;
    }
    const after = index > 0 ? ordered[index - 1][0] : Number.NEGATIVE_INFINITY;
    while (index < ordered.length && !ordered[index][1]) index += 1;
    const before = index < ordered.length ? ordered[index][0] : Number.POSITIVE_INFINITY;
    gaps.push({ after, before });
  }
  return gaps;
}

export function timestampIsInTrendDataGap(timestamp: number, gaps: TrendDataGapRange[]): boolean {
  return Number.isFinite(timestamp) && gaps.some((gap) => timestamp > gap.after && timestamp < gap.before);
}

function tooltipAnchorForPoint(chart: HighchartsChart, point: HighchartsPoint): { x: number; y: number } | null {
  if (typeof point.plotX !== 'number' || typeof point.plotY !== 'number') return null;
  const xAxisOffset = chart.inverted ? 0 : (point.series?.xAxis?.left ?? chart.plotLeft ?? 0) - (chart.plotLeft ?? 0);
  const yAxisOffset = chart.inverted ? 0 : (point.series?.yAxis?.top ?? chart.plotTop ?? 0) - (chart.plotTop ?? 0);
  return { x: point.plotX + xAxisOffset, y: point.plotY + yAxisOffset };
}

function rectanglesOverlap(left: ChartRectangle, right: ChartRectangle): boolean {
  return left.left < right.right && left.right > right.left
    && left.top < right.bottom && left.bottom > right.top;
}

function cachedMarkerOverlapsTooltipAbove(
  rectangles: ChartRectangle[],
  tooltip: ChartRectangle,
  anchorY: number
): boolean {
  let low = 0;
  let high = rectangles.length;
  while (low < high) {
    const middle = (low + high) >>> 1;
    if (rectangles[middle].right <= tooltip.left) low = middle + 1;
    else high = middle;
  }
  for (let index = low; index < rectangles.length; index += 1) {
    const marker = rectangles[index];
    if (marker.left >= tooltip.right) break;
    if ((marker.top + marker.bottom) / 2 < anchorY && rectanglesOverlap(tooltip, marker)) return true;
  }
  return false;
}

/**
 * Highcharts 4 places a shared RMS tooltip beside its anchor. If that native
 * rectangle would cover an event marker above the hovered trend, ask the
 * native positioner for its below-point layout instead. Event-marker tooltips
 * and ordinary trend tooltips retain Highcharts' normal placement.
 */
export function eventAwareTrendTooltipPositioner(
  this: HighchartsTooltipContext,
  width: number,
  height: number,
  point: HighchartsTooltipAnchor
): HighchartsTooltipPosition {
  const normal = this.getPosition(width, height, point);
  const chart = this.chart;
  const anchor = {
    x: point.plotX + (chart.plotLeft ?? 0),
    y: point.plotY + (chart.plotTop ?? 0)
  };
  const activeEventAnchor = chart._pqEventTooltipPoint
    ? tooltipAnchorForPoint(chart, chart._pqEventTooltipPoint)
    : null;
  if (activeEventAnchor
    && Math.abs(activeEventAnchor.x - point.plotX) <= 1
    && Math.abs(activeEventAnchor.y - point.plotY) <= 1) return normal;

  const normalRectangle = {
    left: normal.x,
    right: normal.x + width,
    top: normal.y,
    bottom: normal.y + height
  };
  const overlapsMarkerAbove = cachedMarkerOverlapsTooltipAbove(
    chart._pqEventMarkerRectangles ?? [],
    normalRectangle,
    anchor.y
  );
  if (!overlapsMarkerAbove) return normal;

  // Shared tooltips set len > 1, making Highcharts interpret ttBelow as a
  // left/right preference. Temporarily use its single-anchor branch so the
  // alternate remains bounded and keeps the legacy callout geometry.
  const sharedLength = this.len;
  try {
    this.len = 1;
    return this.getPosition(width, height, { ...point, ttBelow: true });
  } finally {
    this.len = sharedLength;
  }
}

export function eventMarkerAriaLabel(
  seriesName: string | undefined,
  timestampMilliseconds: number,
  durationMilliseconds: number,
  classification?: string
): string {
  const eventName = seriesName?.trim() || 'Power quality event';
  const timestamp = Number.isFinite(timestampMilliseconds)
    ? formatUtc(timestampMilliseconds / 1000, true)
    : 'unknown time';
  const duration = Number.isFinite(durationMilliseconds)
    ? `duration ${formatEventDuration(durationMilliseconds)}`
    : 'unknown duration';
  const classificationText = classification?.trim()
    ? ` Classification: ${classification.trim()}.`
    : '';
  return `${eventName} at ${timestamp}, ${duration}.${classificationText} Open captured waveforms.`;
}

/**
 * Prepare Highcharts 4 event-marker graphics for predictable pointer and
 * keyboard interaction. Marker mouse movement must not bubble into the
 * chart-wide shared RMS search, which can replace the exact event tooltip.
 * Actionable markers mirror their click action on Enter and Space.
 */
export function makeEventMarkersKeyboardAccessible(chart: HighchartsChart): void {
  const markerRadius = EVENT_TOOLTIP_MARKER_RADIUS + EVENT_TOOLTIP_MARKER_CLEARANCE;
  const markerRectangles: ChartRectangle[] = [];
  for (const series of chart.series ?? []) {
    for (const point of series.points ?? []) {
      const element = point.graphic?.element;
      const duration = point.duration_ms ?? point.options?.duration_ms;
      const timestamp = point.x ?? point.options?.x;
      const classification = point.classification ?? point.options?.classification;
      if (!element) continue;
      if (typeof duration === 'number' && series.visible !== false) {
        const pointAnchor = tooltipAnchorForPoint(chart, point);
        if (pointAnchor) {
          const x = pointAnchor.x + (chart.plotLeft ?? 0);
          const y = pointAnchor.y + (chart.plotTop ?? 0);
          markerRectangles.push({
            left: x - markerRadius,
            right: x + markerRadius,
            top: y - markerRadius,
            bottom: y + markerRadius
          });
        }
      }
      const existing = eventMarkerBindings.get(element);
      if (typeof duration !== 'number') {
        if (existing?.keydownListener) element.removeEventListener('keydown', existing.keydownListener);
        if (existing?.mousemoveListener) element.removeEventListener('mousemove', existing.mousemoveListener);
        eventMarkerBindings.delete(element);
        element.removeAttribute('tabindex');
        element.removeAttribute('role');
        element.removeAttribute('aria-label');
        element.removeAttribute('aria-current');
        element.removeAttribute('data-pq-event-marker');
        continue;
      }

      if (existing?.point !== point) {
        if (existing?.keydownListener) element.removeEventListener('keydown', existing.keydownListener);
        if (existing?.mousemoveListener) element.removeEventListener('mousemove', existing.mousemoveListener);
        const mousemoveListener = (event: MouseEvent) => event.stopPropagation();
        element.addEventListener('mousemove', mousemoveListener);
        eventMarkerBindings.set(element, { point, mousemoveListener });
      }

      if (typeof point.firePointEvent !== 'function' || duration <= 0) {
        const binding = eventMarkerBindings.get(element);
        if (binding?.keydownListener) {
          element.removeEventListener('keydown', binding.keydownListener);
          delete binding.keydownListener;
        }
        element.removeAttribute('tabindex');
        element.removeAttribute('role');
        element.removeAttribute('aria-label');
        element.removeAttribute('aria-current');
        element.removeAttribute('data-pq-event-marker');
        continue;
      }

      element.setAttribute('tabindex', '0');
      element.setAttribute('role', 'button');
      element.setAttribute('data-pq-event-marker', '');
      if (point.selected === true) element.setAttribute('aria-current', 'true');
      else element.removeAttribute('aria-current');
      element.setAttribute('aria-label', eventMarkerAriaLabel(
        point.series?.name ?? series.name,
        timestamp ?? Number.NaN,
        duration,
        classification
      ));

      const binding = eventMarkerBindings.get(element);
      if (binding?.keydownListener) continue;

      const keydownListener = (event: KeyboardEvent) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        point.firePointEvent?.('click', event);
      };
      element.addEventListener('keydown', keydownListener);
      if (binding) binding.keydownListener = keydownListener;
    }
  }
  chart._pqEventMarkerRectangles = markerRectangles.sort((left, right) => left.left - right.left);
}

function highchartsControlPalette(dark: boolean) {
  return {
    surface: dark ? '#18232c' : '#ffffff',
    raisedSurface: dark ? '#202c36' : '#f7f9fa',
    selectedSurface: dark ? '#334653' : '#e8f2fa',
    border: dark ? '#617585' : '#b9c3ca',
    accent: dark ? '#78b9f2' : '#337ab7',
    text: dark ? '#e8edf2' : '#333333'
  };
}

export function highchartsNavigationTheme(dark: boolean) {
  const { surface, raisedSurface, selectedSurface, border, accent, text } = highchartsControlPalette(dark);

  return {
    buttonOptions: {
      width: 28,
      height: 28,
      symbolSize: 12,
      symbolX: 14,
      symbolY: 14,
      symbolFill: 'none',
      symbolStroke: text,
      symbolStrokeWidth: 2,
      theme: {
        zIndex: 20,
        fill: surface,
        stroke: border,
        'stroke-width': 1,
        r: 4,
        style: { color: text, cursor: 'pointer' },
        states: {
          hover: { fill: raisedSurface, stroke: accent },
          select: { fill: selectedSurface, stroke: accent }
        }
      }
    },
    menuStyle: {
      padding: '4px 0',
      border: `1px solid ${border}`,
      borderRadius: '4px',
      background: surface,
      overflow: 'hidden',
      boxShadow: dark ? '0 6px 18px rgba(0, 0, 0, 0.45)' : '0 6px 18px rgba(0, 0, 0, 0.16)'
    },
    menuItemStyle: {
      padding: '6px 12px',
      background: 'none',
      color: text,
      fontFamily: 'Arial, Helvetica, sans-serif',
      fontSize: '14px',
      lineHeight: '1.35',
      whiteSpace: 'nowrap'
    },
    menuItemHoverStyle: {
      background: raisedSurface,
      color: text
    }
  };
}

export function highchartsResetZoomButtonOptions(dark: boolean) {
  const { surface, raisedSurface, border, accent, text } = highchartsControlPalette(dark);

  return {
    relativeTo: 'chart',
    // The export control is 28 px wide with a 10 px chart inset and 3 px gap.
    position: { align: 'right', verticalAlign: 'top', x: -41, y: 10 },
    theme: {
      zIndex: 20,
      fill: surface,
      stroke: border,
      'stroke-width': 1,
      r: 4,
      padding: 7,
      style: {
        color: text,
        cursor: 'pointer',
        fontFamily: 'Arial, Helvetica, sans-serif',
        fontSize: '12px',
        fontWeight: 'normal'
      },
      states: {
        hover: { fill: raisedSurface, stroke: accent, style: { color: text } }
      }
    }
  };
}

interface ExportMenuRectangle {
  bottom: number;
  height: number;
  left: number;
  right: number;
  top: number;
  width: number;
}

const EXPORT_MENU_GAP = 3;
const EXPORT_MENU_VIEWPORT_MARGIN = 8;

export function highchartsExportMenuViewportPosition(
  buttonRect: ExportMenuRectangle,
  menuRect: ExportMenuRectangle,
  viewportWidth: number,
  viewportHeight: number
) {
  const maxWidth = Math.max(0, viewportWidth - 2 * EXPORT_MENU_VIEWPORT_MARGIN);
  const maxHeight = Math.max(0, viewportHeight - 2 * EXPORT_MENU_VIEWPORT_MARGIN);
  const width = Math.min(menuRect.width, maxWidth);
  const height = Math.min(menuRect.height, maxHeight);
  const maximumLeft = Math.max(EXPORT_MENU_VIEWPORT_MARGIN, viewportWidth - EXPORT_MENU_VIEWPORT_MARGIN - width);
  const left = Math.min(maximumLeft, Math.max(EXPORT_MENU_VIEWPORT_MARGIN, buttonRect.right - width));
  const below = buttonRect.bottom + EXPORT_MENU_GAP;
  const above = buttonRect.top - height - EXPORT_MENU_GAP;
  const top = below + height <= viewportHeight - EXPORT_MENU_VIEWPORT_MARGIN
    ? below
    : Math.max(EXPORT_MENU_VIEWPORT_MARGIN, above);
  return { left, top, maxWidth, maxHeight };
}

export function floatHighchartsExportMenu(menu: HTMLElement, button: Element): void {
  if (menu.parentElement !== document.body) document.body.append(menu);
  Object.assign(menu.style, {
    bottom: 'auto',
    maxHeight: `calc(100vh - ${2 * EXPORT_MENU_VIEWPORT_MARGIN}px)`,
    maxWidth: `calc(100vw - ${2 * EXPORT_MENU_VIEWPORT_MARGIN}px)`,
    overflow: 'auto',
    padding: '0',
    pointerEvents: 'auto',
    position: 'fixed',
    right: 'auto',
    zIndex: '10000'
  });
  menu.dataset.pqFloating = 'true';
  const position = highchartsExportMenuViewportPosition(
    button.getBoundingClientRect(),
    menu.getBoundingClientRect(),
    window.innerWidth,
    window.innerHeight
  );
  menu.style.left = `${position.left}px`;
  menu.style.top = `${position.top}px`;
}

export function bindHighchartsResetZoom(
  chart: HighchartsChart,
  callback: (() => void) | undefined,
  visible = false
): boolean {
  if (!callback) return false;
  if (visible && !chart.resetZoomButton) chart.showResetZoom?.();
  const button = chart.resetZoomButton;
  if (!button?.on) return false;
  const element = button.element;
  if (!element) {
    button.on('click', (event) => {
      event?.preventDefault();
      callback();
    });
    return true;
  }

  let binding = resetZoomBindings.get(element);
  if (!binding) {
    binding = {
      callback,
      keydown: (event: KeyboardEvent) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        resetZoomBindings.get(element)?.callback();
      }
    };
    resetZoomBindings.set(element, binding);
    button.on('click', (event) => {
      event?.preventDefault();
      resetZoomBindings.get(element)?.callback();
    });
    element.addEventListener('keydown', binding.keydown as EventListener);
  } else {
    binding.callback = callback;
  }
  element.setAttribute('role', 'button');
  element.setAttribute('tabindex', '0');
  element.setAttribute('aria-label', 'Reset zoom');
  return true;
}

interface ResetZoomBinding {
  callback: () => void;
  keydown: (event: KeyboardEvent) => void;
}

const resetZoomBindings = new WeakMap<Element, ResetZoomBinding>();
let closeActiveFloatingExportMenu: (() => void) | null = null;

export function installFloatingHighchartsExportMenu(chart: HighchartsChart): () => void {
  const originalContextMenu = chart.contextMenu;
  if (!originalContextMenu) return () => undefined;
  let activeMenu: HTMLElement | null = null;
  let activeButton = chart.exportSVGElements?.[0] ?? null;
  let menuObserver: MutationObserver | null = null;
  let repositionListening = false;
  let openOnPointerDown = false;
  let menuButtonOverlay: HTMLButtonElement | null = null;
  let menuButtonHovered = false;
  const refreshMenuButtonState = () => {
    if (chart.openMenu) {
      activeButton?.setState?.(2);
      return;
    }
    activeButton?.setState?.(0);
    if (menuButtonHovered) dispatchContextButtonHover();
  };
  const stopRepositioning = () => {
    if (!repositionListening) return;
    window.removeEventListener('resize', reposition);
    window.removeEventListener('scroll', reposition, true);
    repositionListening = false;
  };
  const closeMenu = () => {
    if (activeMenu) activeMenu.style.display = 'none';
    chart.openMenu = false;
    menuButtonOverlay?.setAttribute('aria-expanded', 'false');
    refreshMenuButtonState();
    stopRepositioning();
    if (closeActiveFloatingExportMenu === closeMenu) closeActiveFloatingExportMenu = null;
  };
  const reposition = () => {
    const button = menuButtonOverlay ?? activeButton?.element;
    if (!chart.openMenu || !activeMenu || !button || activeMenu.style.display === 'none') {
      stopRepositioning();
      return;
    }
    const bounds = button.getBoundingClientRect();
    if (bounds.right <= 0 || bounds.bottom <= 0 || bounds.left >= window.innerWidth || bounds.top >= window.innerHeight) {
      closeMenu();
      return;
    }
    floatHighchartsExportMenu(activeMenu, button);
  };
  const startRepositioning = () => {
    if (repositionListening) return;
    window.addEventListener('resize', reposition);
    window.addEventListener('scroll', reposition, true);
    repositionListening = true;
  };
  const contextButton = activeButton?.element;
  const chartContainer = chart.container;
  const contextButtonBackground = contextButton
    ? Array.from(contextButton.children).find((child) => child.localName === 'rect')
    : undefined;
  let pointerActivationPending = false;
  let overlayMutationObserver: MutationObserver | null = null;
  const contextButtonAttributes = contextButton
    ? new Map(
      ['tabindex', 'aria-hidden', 'focusable', 'role', 'aria-label', 'aria-expanded']
        .map((name) => [name, contextButton.getAttribute(name)] as const)
    )
    : null;
  const contextButtonPointerEvents = contextButton instanceof SVGElement
    ? contextButton.style.pointerEvents
    : null;
  const contextButtonBounds = () => {
    for (const element of [contextButtonBackground, contextButton]) {
      if (!element) continue;
      const bounds = element.getBoundingClientRect();
      if (bounds.width > 0 && bounds.height > 0) return bounds;
    }
    return null;
  };
  const dispatchContextButtonHover = () => {
    if (!contextButton) return;
    const buttonWindow = contextButton.ownerDocument.defaultView;
    const ButtonEvent = buttonWindow?.Event ?? Event;
    // Highcharts 4 paints its hover theme only from these native handlers;
    // setState(1) records the state but leaves the normal colours in place.
    for (const eventType of ['mouseenter', 'mouseover']) {
      contextButton.dispatchEvent(new ButtonEvent(eventType, {
        bubbles: false,
        cancelable: false,
        composed: false
      }));
    }
  };
  const dispatchContextButtonClick = (source?: MouseEvent) => {
    if (!contextButton) return;
    const buttonWindow = contextButton.ownerDocument.defaultView;
    const ButtonMouseEvent = buttonWindow?.MouseEvent ?? MouseEvent;
    contextButton.dispatchEvent(new ButtonMouseEvent('click', {
      bubbles: false,
      cancelable: true,
      composed: false,
      detail: source?.detail ?? 0,
      screenX: source?.screenX ?? 0,
      screenY: source?.screenY ?? 0,
      clientX: source?.clientX ?? 0,
      clientY: source?.clientY ?? 0,
      ctrlKey: source?.ctrlKey ?? false,
      shiftKey: source?.shiftKey ?? false,
      altKey: source?.altKey ?? false,
      metaKey: source?.metaKey ?? false,
      button: source?.button ?? 0,
      buttons: source?.buttons ?? 0
    }));
  };
  const rememberOpenState = (event: Event) => {
    if ((event as MouseEvent).button !== 0) return;
    openOnPointerDown = chart.openMenu === true;
    pointerActivationPending = true;
  };
  const stopOverlayMousePropagation = (event: Event) => event.stopPropagation();
  const cancelPointerActivation = () => {
    pointerActivationPending = false;
  };
  const activateFromOverlay = (event: Event) => {
    const mouseEvent = event as MouseEvent;
    if (mouseEvent.button !== 0) return;
    if (mouseEvent.detail === 0 || !pointerActivationPending) openOnPointerDown = chart.openMenu === true;
    pointerActivationPending = false;
    event.preventDefault();
    event.stopPropagation();
    dispatchContextButtonClick(mouseEvent);
  };
  const handleMenuButtonPointerEnter = () => {
    menuButtonHovered = true;
    refreshMenuButtonState();
  };
  const handleMenuButtonPointerLeave = () => {
    menuButtonHovered = false;
    pointerActivationPending = false;
    refreshMenuButtonState();
  };
  let overlayResizeObserver: ResizeObserver | null = null;
  const positionOverlay = () => {
    if (!menuButtonOverlay || !chartContainer) return;
    const buttonBounds = contextButtonBounds();
    const containerBounds = chartContainer.getBoundingClientRect();
    const geometry = buttonBounds
      ? [buttonBounds.left, buttonBounds.top, buttonBounds.width, buttonBounds.height]
      : [];
    if (!buttonBounds || geometry.some((value) => !Number.isFinite(value)) || buttonBounds.width <= 0 || buttonBounds.height <= 0) {
      menuButtonOverlay.style.visibility = 'hidden';
      return;
    }
    menuButtonOverlay.style.left = `${buttonBounds.left - containerBounds.left - chartContainer.clientLeft + chartContainer.scrollLeft}px`;
    menuButtonOverlay.style.top = `${buttonBounds.top - containerBounds.top - chartContainer.clientTop + chartContainer.scrollTop}px`;
    menuButtonOverlay.style.width = `${buttonBounds.width}px`;
    menuButtonOverlay.style.height = `${buttonBounds.height}px`;
    menuButtonOverlay.style.visibility = 'visible';
  };
  if (contextButton && chartContainer) {
    contextButton.setAttribute('tabindex', '-1');
    contextButton.setAttribute('aria-hidden', 'true');
    contextButton.setAttribute('focusable', 'false');
    contextButton.removeAttribute('role');
    contextButton.removeAttribute('aria-label');
    contextButton.removeAttribute('aria-expanded');
    if (contextButton instanceof SVGElement) contextButton.style.pointerEvents = 'none';

    menuButtonOverlay = document.createElement('button');
    menuButtonOverlay.type = 'button';
    menuButtonOverlay.dataset.pqMenuHitTarget = 'true';
    menuButtonOverlay.setAttribute('aria-label', 'Chart menu');
    menuButtonOverlay.setAttribute('aria-haspopup', 'menu');
    menuButtonOverlay.setAttribute('aria-expanded', 'false');
    menuButtonOverlay.title = 'Chart menu';
    Object.assign(menuButtonOverlay.style, {
      appearance: 'none',
      background: 'transparent',
      border: '0',
      borderRadius: '4px',
      cursor: 'pointer',
      margin: '0',
      padding: '0',
      pointerEvents: 'auto',
      position: 'absolute',
      touchAction: 'manipulation',
      visibility: 'hidden',
      zIndex: '21'
    });
    menuButtonOverlay.addEventListener('pointerdown', rememberOpenState);
    menuButtonOverlay.addEventListener('pointercancel', cancelPointerActivation);
    menuButtonOverlay.addEventListener('mousedown', stopOverlayMousePropagation);
    menuButtonOverlay.addEventListener('mouseup', stopOverlayMousePropagation);
    menuButtonOverlay.addEventListener('click', activateFromOverlay);
    menuButtonOverlay.addEventListener('pointerenter', handleMenuButtonPointerEnter);
    menuButtonOverlay.addEventListener('pointerleave', handleMenuButtonPointerLeave);
    chartContainer.appendChild(menuButtonOverlay);
    positionOverlay();
    window.addEventListener('resize', positionOverlay);
    if (typeof ResizeObserver !== 'undefined') {
      try {
        overlayResizeObserver = new ResizeObserver(positionOverlay);
        overlayResizeObserver.observe(chartContainer);
      } catch {
        overlayResizeObserver?.disconnect();
        overlayResizeObserver = null;
      }
    }
    if (typeof MutationObserver !== 'undefined') {
      try {
        overlayMutationObserver = new MutationObserver(positionOverlay);
        overlayMutationObserver.observe(contextButton, {
          attributes: true,
          attributeFilter: ['height', 'transform', 'width', 'x', 'y'],
          subtree: true
        });
      } catch {
        overlayMutationObserver?.disconnect();
        overlayMutationObserver = null;
      }
    }
  }
  chart.contextMenu = (...args: unknown[]) => {
    if (openOnPointerDown || (chart.openMenu && activeMenu?.style.display !== 'none')) {
      openOnPointerDown = false;
      closeMenu();
      // Highcharts 4 selects the context button immediately after its
      // contextMenu call returns. Reset it after that legacy callback so a
      // close-toggle cannot leave the hamburger looking pressed.
      queueMicrotask(() => {
        if (!chart.openMenu) refreshMenuButtonState();
      });
      return;
    }
    openOnPointerDown = false;
    closeActiveFloatingExportMenu?.();
    originalContextMenu.apply(chart, args);
    const menuClassName = typeof args[0] === 'string' ? args[0] : '';
    const buttonControl = args[6] as HighchartsControlElement | undefined;
    const button = buttonControl?.element;
    const menu = menuClassName
      ? (chart as unknown as Record<string, unknown>)[`cache-${menuClassName}`]
      : undefined;
    if (menu instanceof HTMLElement && button) {
      activeMenu = menu;
      activeButton = buttonControl ?? activeButton;
      menuButtonOverlay?.setAttribute('aria-expanded', 'true');
      refreshMenuButtonState();
      floatHighchartsExportMenu(menu, menuButtonOverlay ?? button);
      startRepositioning();
      menuObserver?.disconnect();
      menuObserver = new MutationObserver(() => {
        if (menu.style.display === 'none') {
          menuButtonOverlay?.setAttribute('aria-expanded', 'false');
          refreshMenuButtonState();
          stopRepositioning();
          if (closeActiveFloatingExportMenu === closeMenu) closeActiveFloatingExportMenu = null;
        }
      });
      menuObserver.observe(menu, { attributes: true, attributeFilter: ['style'] });
      closeActiveFloatingExportMenu = closeMenu;
    }
  };
  return () => {
    closeMenu();
    menuObserver?.disconnect();
    menuObserver = null;
    overlayResizeObserver?.disconnect();
    overlayResizeObserver = null;
    overlayMutationObserver?.disconnect();
    overlayMutationObserver = null;
    window.removeEventListener('resize', positionOverlay);
    if (menuButtonOverlay) {
      menuButtonOverlay.removeEventListener('pointerdown', rememberOpenState);
      menuButtonOverlay.removeEventListener('pointercancel', cancelPointerActivation);
      menuButtonOverlay.removeEventListener('mousedown', stopOverlayMousePropagation);
      menuButtonOverlay.removeEventListener('mouseup', stopOverlayMousePropagation);
      menuButtonOverlay.removeEventListener('click', activateFromOverlay);
      menuButtonOverlay.removeEventListener('pointerenter', handleMenuButtonPointerEnter);
      menuButtonOverlay.removeEventListener('pointerleave', handleMenuButtonPointerLeave);
      menuButtonOverlay.disabled = true;
      menuButtonOverlay.style.pointerEvents = 'none';
      menuButtonOverlay.remove();
    }
    menuButtonOverlay = null;
    if (contextButton) {
      for (const [name, value] of contextButtonAttributes ?? []) {
        if (value === null) contextButton.removeAttribute(name);
        else contextButton.setAttribute(name, value);
      }
      if (contextButton instanceof SVGElement && contextButtonPointerEvents !== null) {
        contextButton.style.pointerEvents = contextButtonPointerEvents;
      }
    }
    chart.contextMenu = originalContextMenu;
    activeMenu = null;
    activeButton = null;
  };
}
